import * as React from 'react';
import { Component, Fragment } from 'react';
import { computed, toJS } from 'mobx';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import get from 'lodash/get';
import isString from 'lodash/isString';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import Disclaimers from "./Disclaimers";
import Translate, { translate } from "./Translate";
import ChartBarChart from "./ChartBarChart";
import ChartLineChart from "./ChartLineChart";
import formatter from "./formatter";
import { sortString, sortDate, sortNumber } from '../utils';

const ChartWrapper = styled<any, any>('div')`
    ${propStyles};
`;

ChartWrapper.defaultProps = {
    marginY: "md",
    overflow: "hidden"
};

const ChartHeading = styled<any, any>('h2')`
    ${propStyles};
`;

ChartHeading.defaultProps = {
    color: "body",
    fontFamily: "heading",
    fontWeight: "bold",
    fontSize: "md"
};

const ChartHeader = styled<any, any>('div')`
    ${propStyles};
`;

ChartHeader.defaultProps = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "xs",
    margin: "0 0 1em 0"
};

const AsAtDate = styled<any, any>('div')`
    ${propStyles};
`;

AsAtDate.defaultProps = {
    fontSize: "xs"
};

@inject('dataStore')
@inject('translations')
@observer
class Chart extends Component<any, any> {
    @computed
    get locale() {
        return this.props.dataStore.culture;
    }

    @computed
    get fundCurrency() {
        return get(this.props.shareClass, `properties_pub.${this.props.dataStore.config.fundCurrencyProperty || 'ccy'}.value`);
    }

    getDataSet = (dataConfig) => {
        const { property, code, ccy, classification, periodicity, key, index, sort } = dataConfig;

        let dataSet = [];

        if (property) {
            dataSet = get(this.props.shareClass, property, []);
        }

        if (code) {
            dataSet = dataSet.filter(d => d.code === code);
        }

        // TODO: Decide how to handle ccy. 
        // Is it config or ccy of shareclass or some other business logic

        if (ccy) {
            // dataSet = dataSet.filter(d => d.ccy === ccy);
        } else {
            // dataSet = dataSet.filter(d => d.ccy === this.props.shareClass.properties_pub.ccy);
        }

        if (classification) {
            dataSet = dataSet.filter(d => d.classification === classification);
        }

        if (periodicity) {
            dataSet = dataSet.filter(d => d.periodicity === periodicity);
        }

        // We should end up with one data object, so get the first item in array
        const [data = {}] = dataSet;

        // Get the values from data
        let { values = [] } = data;

        // Return a single value at index
        if (index >= 0 && key) {
            return toJS(get(values, `[${index}].${key}`));
        }

        // Convert to plain array for sort operations
        values = toJS(values);

        if (sort && sort.type === "date") {
            values.sort((a, b) => sortDate(a[sort.key], b[sort.key]));
        }

        if (sort && sort.type === "string") {
            values.sort((a, b) => sortString(a[sort.key], b[sort.key]));
        }

        if (sort && sort.type === "number") {
            values.sort((a, b) => sortNumber(a[sort.key], b[sort.key]));
        }

        if (sort && sort.direction === "DESC") {
            values.reverse();
        }

        // Sort by a specified order
        if (sort && sort.order && sort.order.length > 0) {
            values = values.map(value => {
                const orderIndex = sort.order.indexOf(value[sort.key]);
                value.order = (orderIndex > -1) ? orderIndex : sort.order.length;
                return value;
            })
            .sort((a, b) => sortNumber(a.order, b.order));
        }

        return values;
    }

    @computed
    get data() {
        return this.getDataSet(this.props.chart.data);
    }

    @computed
    get chartDisclaimers() {
        const disclaimers = this.disclaimerTypes
            .map(d => {
                if (d.linked) {
                    return get(this.disclaimersDictionary, d.disclaimerType);
                } else {
                    return get(this.props.dataStore.disclaimersDictionary, `*.${d.disclaimerType}`);
                }
            })
            .filter(d => d !== undefined);

        return disclaimers;
    }

    @computed
    get noDataDisclaimers() {
        const disclaimers = this.noDataDisclaimerTypes
            .map(d => {
                if (d.linked) {
                    return get(this.disclaimersDictionary, d.disclaimerType);
                } else {
                    return get(this.props.dataStore.disclaimersDictionary, `*.${d.disclaimerType}`);
                }
            })
            .filter(d => d !== undefined);

        return disclaimers;
    }

    @computed
    get config() {
        return this.props.chart.config || {};
    }

    @computed
    get disclaimerTypes() {
        return this.props.chart.disclaimerTypes || [];
    }

    @computed
    get noDataDisclaimerTypes() {
        return this.props.chart.noDataDisclaimerTypes || [];
    }

    @computed
    get disclaimersDictionary() {
        const disclaimersDictionary = {};
        const { disclaimers = {} } = this.props.shareClass;

        for (let d in disclaimers) {
            if (d) {
                const { disclaimer = '' } = disclaimers[d].find(dc => dc.culture === this.props.dataStore.culture) || {};
                disclaimersDictionary[d] = disclaimer;
            }
        }

        return disclaimersDictionary;
    }

    @computed
    get xAxis() {
        const { xAxis } = this.config;

        return toJS(xAxis);
    }

    @computed
    get yAxis() {
        const { yAxis } = this.config;

        return toJS(yAxis);
    }

    @computed
    get series() {
        const { series = [] } = this.config;

        return toJS(series);
    }

    @computed
    get asAtDate() {
        const { asAtDate = {} } = this.props.chart;
        const { data = {}, format = {}, label } = asAtDate;
        const asAtDateValue = this.getDataSet(data);

        if (asAtDateValue) {
            return {
                label: label,
                value: asAtDateValue
                    ? formatter(format.type, this.locale, this.fundCurrency, format.options, asAtDateValue)
                    : asAtDateValue
            };
        }

        return null;
    }

    @computed
    get performancePeriods() {
        const { performancePeriods = [] } = this.props.chart;

        return performancePeriods;
    }

    getThemeColor = (color) => {
        return get(this.props.theme, `color.${color}`, color);
    }

    getLabelName = (label) => {
        if (isString(label)) {
            return {
                value: translate(this.props.translations, label)
            };
        }

        const labelValue = get(label, 'value', label);
        const translatedLabelValue = translate(this.props.translations, labelValue);

        return {
            ...label,
            value: translatedLabelValue
        };
    }

    getSeriesName = (name, nameProperty) => {
        const seriesName = get(this.props.shareClass.properties_pub, `${nameProperty}.value`, name);
        const translatedSeriesName = translate(this.props.translations, seriesName);
        return translatedSeriesName;
    }

    render() {
        const { theme, chart, showHeading, isMobile } = this.props;
        const {
            data,
            config,
            xAxis,
            yAxis,
            series,
            getThemeColor,
            getLabelName,
            getSeriesName,
            chartDisclaimers,
            noDataDisclaimers,
            locale,
            fundCurrency,
            asAtDate,
            performancePeriods
        } = this;

        return (
            <ChartWrapper
                {...theme.ChartsWrapper}
            >
                {
                    showHeading && (
                        <ChartHeading
                            {...theme.ChartHeading}
                        >
                            <Translate>{chart.label}</Translate>
                        </ChartHeading>
                    )
                }
                {
                    data.length > 0
                        ? (
                            <Fragment>
                                <ChartHeader
                                    {...theme.ChartHeader}
                                >
                                    {
                                        performancePeriods && (
                                            <div>
                                                {performancePeriods.map(p => <span key={p.label}>&nbsp;&nbsp;{p.label}&nbsp;&nbsp;</span>)}
                                            </div>
                                        )
                                    }
                                    {
                                        asAtDate && (
                                            <AsAtDate
                                                {...theme.ChartAsAtDate}
                                            >
                                                <Translate>{asAtDate.label}</Translate> {asAtDate.value}
                                            </AsAtDate>
                                        )
                                    }
                                </ChartHeader>
                                {
                                    chart.type === "BarChart" && (
                                        <ChartBarChart
                                            config={config}
                                            data={data}
                                            xAxis={isMobile ? yAxis : xAxis}
                                            yAxis={isMobile ? xAxis : yAxis}
                                            layout={isMobile ? "vertical" : "horizontal"}
                                            minHeight={isMobile ? 400 : undefined}
                                            aspect={isMobile ? undefined : 3}
                                            series={series}
                                            getThemeColor={getThemeColor}
                                            getLabelName={getLabelName}
                                            getSeriesName={getSeriesName}
                                            locale={locale}
                                            fundCurrency={fundCurrency}
                                            isMobile={isMobile}
                                        />
                                    )
                                }
                                {
                                    chart.type === "LineChart" && (
                                        <ChartLineChart
                                            config={config}
                                            data={data}
                                            xAxis={xAxis}
                                            yAxis={yAxis}
                                            series={series}
                                            getThemeColor={getThemeColor}
                                            getLabelName={getLabelName}
                                            getSeriesName={getSeriesName}
                                            locale={locale}
                                            fundCurrency={fundCurrency}
                                        />
                                    )
                                }
                                <Disclaimers 
                                    marginX="0" 
                                    disclaimers={chartDisclaimers} 
                                    theme={{
                                        ...theme,
                                        DisclaimersList: theme.ChartDisclaimersList,
                                        DisclaimerListItem: theme.ChartDisclaimerListItem,
                                        DisclaimerCharacter: theme.ChartDisclaimerCharacter,
                                        DisclaimerContent: theme.ChartDisclaimerContent
                                    }}
                                />
                            </Fragment>
                        )
                        : (
                            <Disclaimers
                                marginX="0"
                                disclaimers={noDataDisclaimers}
                                theme={{
                                    ...theme,
                                    DisclaimersList: theme.ChartNoDataDisclaimersList,
                                    DisclaimerListItem: theme.ChartNoDataDisclaimerListItem,
                                    DisclaimerCharacter: theme.ChartNoDataDisclaimerCharacter,
                                    DisclaimerContent: theme.ChartNoDataDisclaimerContent
                                }}
                            />
                        )

                }
            </ChartWrapper>
        );
    }
}

export default withTheme(Chart) as any;


// WEBPACK FOOTER //
// ./src/Components/Chart.tsx